import type { Ref } from 'vue'
import { useMenuPath } from './useMenuPath'

const EDIT_CAMPAIGN_MENU_PATHS = ['/campaigns/edit', '/report/campaigns'] as const

/**
 * Composable for menu color logic
 * Handles color retrieval based on menu paths
 * Resolution mirrors TitleResolver: exact match, then recursive
 * segment stripping to the nearest menu ancestor, then CONFIG.BASE_COLOR
 */
export function useMenuColor(menuColors: Ref<Map<string, string>>) {
  const { isEditCampaignPath } = useMenuPath()

  function getEditCampaignColor(): string | undefined {
    for (const menuPath of EDIT_CAMPAIGN_MENU_PATHS) {
      const color = menuColors.value.get(menuPath)
      if (color) return color
    }
    return undefined
  }

  function getAncestorColor(path: string): string | undefined {
    let currentPath = path
    while (currentPath !== '' && currentPath !== '/') {
      const lastSlashIndex = currentPath.lastIndexOf('/')
      if (lastSlashIndex <= 0) break // at root: '/' is never a color prefix
      currentPath = currentPath.substring(0, lastSlashIndex)
      const color = menuColors.value.get(currentPath)
      if (color) return color
    }
    return undefined
  }

  /**
   * Get color for a given path
   * @param path - Route path
   * @returns Color hex string
   */
  function getPathColor(path: string): string {
    if (isEditCampaignPath(path)) {
      return getEditCampaignColor() ?? CONFIG.BASE_COLOR
    }
    return menuColors.value.get(path) ?? getAncestorColor(path) ?? CONFIG.BASE_COLOR
  }

  return {
    getPathColor
  }
}
